import React, { useState, useEffect, useRef } from "react";
import "./practiceInfo.scss";
import CgFormCreator from "../../../common/CgFormCreator";
import PopupModal from "../../../common/PopupModal";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPracticeInfoDetails,
  fetchPracticeInfoPhoneFaxDetails,
  fetchPracticeInfoEmailDetails,
  fetchUpdatePracticeInfoEmailDetailsAPI,
  fetchUpdatePracticeInfoPhoneFaxDetails,
  fetchUpdatePracticeInfoDetailsAPI,
} from "../../../store/PracticeInfo/PracticeInfoActions";
import GoogleMapComponent from "../../../common/GoogleMapComponent";
import { useLocation } from "react-router-dom";
import statesData, {
  convertTenDigitPhoneNumberToNANP,
} from "../../../utils/commonUtilities";
import redAlert from "../../../assets/images/redAlert.svg";

const practiceInfoContactTypeDropdownOptions = [
  { id: 0, value: "OFFICE ADMINISTRATION" },
  { id: 1, value: "MAIN" },
  { id: 2, value: "BILLING" },
  { id: 3, value: "OTHER" },
];

const validPostalCode = /^\d+$/;
const validNumber = /^\d{3}-\d{3}-\d{4}$/;
const validEmail = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
const validUrl =
  /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/[\w-._~:/?#[\]@!$&'()*+,;=]*)?$/i;

const PracticeInfo = () => {
  const [openPopup, setOpenPopup] = useState(false);
  const [currentSection, setCurrentSection] = useState("");
  const [editPracticeInfoPhoneSchema, setEditPracticeInfoPhoneSchema] =
    useState([]);
  const [editPracticeInfoFaxSchema, setEditPracticeInfoFaxSchema] = useState(
    []
  );
  const [editPracticeInfoEmailSchema, setEditPracticeInfoEmailSchema] =
    useState([]);
  const [practiceInfoPhoneData, setPracticeInfoPhoneData] = useState([]);
  const [practiceInfoFaxData, setPracticeInfoFaxData] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [practiceLatLng, setPracticeLatLng] = useState([]);
  const [disableSaveBtn, setDisableSaveBtn] = useState(true);
  const [showError, setShowError] = useState(false);
  const [missingData, setMissingData] = useState(false);

  const {
    practiceInfoDetails,
    isPracticeInfoDataLoading,
    practiceInfoDataError,
    practiceInfoDataErrorMsg,
  } = useSelector((state) => state?.practiceInfoDetails);
  const {
    practiceInfoPhoneFaxDetails,
    isPhoneFaxDataLoading,
    phoneFaxDataError,
    phoneFaxDataErrorMsg,
  } = useSelector((state) => state?.practiceInfoPhoneFaxDetails);
  const {
    practiceInfoEmailDetails,
    isEmailDataLoading,
    emailDataError,
    emailDataErrorMsg,
  } = useSelector((state) => state?.practiceInfoEmailDetails);

  const dispatch = useDispatch();
  const formRef = useRef({});
  const location = useLocation();
  const locationHeaderData = location.pathname.split("/");
  const practiceId = locationHeaderData.length > 2 && locationHeaderData[2];

  const maxEmailLength =
    practiceInfoEmailDetails && practiceInfoEmailDetails?.length > 0
      ? Math.max(
          ...practiceInfoEmailDetails.map((e) => (e.email ? e.email.length : 0))
        )
      : 20;
  const emailWidth = `${maxEmailLength + 4}ch`;

  useEffect(() => {
    if (practiceId) {
      dispatch(fetchPracticeInfoDetails(practiceId));
      dispatch(fetchPracticeInfoPhoneFaxDetails(practiceId));
      dispatch(fetchPracticeInfoEmailDetails(practiceId));
    }
  }, []);

  useEffect(() => {
    const details = {
      lat: Number(practiceInfoDetails?.latitude),
      lng: Number(practiceInfoDetails?.longitude),
    };
    if (practiceInfoDetails?.latitude) {
      setPracticeLatLng([details]);
    }
  }, [practiceInfoDetails]);

  useEffect(() => {
    if (currentSection === "practiceInfoPhone" && editPracticeInfoPhoneSchema) {
      let errorExist = false;
      const emptyPhoneNumberField = editPracticeInfoPhoneSchema.find(
        (item) => item?.name === "number" && item?.defaultValue?.length !== 12
      );
      const contactTypeExist = editPracticeInfoPhoneSchema.filter(
        (item) => item?.name === "contactType" && item?.defaultValue === "MAIN"
      );
      const phoneNumberErorExist = editPracticeInfoPhoneSchema.find(
        (item) => item?.customErrorMsg?.length > 0
      );
      if (editPracticeInfoPhoneSchema?.length === 0) {
        const displayId =
          Date.now().toString(20) + Math.random().toString(20).substring(2, 5);
        const newSchema = getInitialSchema({ phoneType: "PHONE", displayId });
        setEditPracticeInfoPhoneSchema([...newSchema]);
        return;
      }
      if (
        emptyPhoneNumberField ||
        contactTypeExist?.length > 1 ||
        phoneNumberErorExist?.customErrorMsg?.length > 0
      ) {
        setDisableSaveBtn(false);
        setErrorMsg("");
        errorExist = true;
      }
      if (!errorExist) {
        setDisableSaveBtn(true);
        setErrorMsg("");
      }
    }
    if (currentSection === "practiceInfoFax" && editPracticeInfoFaxSchema) {
      let errorExist = false;
      const emptyFaxNumberField = editPracticeInfoFaxSchema.find(
        (item) => item?.name === "number" && item?.defaultValue?.length !== 12
      );
      const contactTypeExist = editPracticeInfoFaxSchema.filter(
        (item) => item?.name === "contactType" && item?.defaultValue === "MAIN"
      );
      const faxNumberErorExist = editPracticeInfoFaxSchema.find(
        (item) => item?.customErrorMsg?.length > 0
      );

      if (
        emptyFaxNumberField ||
        contactTypeExist?.length > 1 ||
        faxNumberErorExist?.customErrorMsg?.length > 0
      ) {
        setDisableSaveBtn(false);
        setErrorMsg("");
        errorExist = true;
      }
      if (!errorExist) {
        setDisableSaveBtn(true);
        setErrorMsg("");
      }
    }
    if (currentSection === "practiceInfoEmail" && editPracticeInfoEmailSchema) {
      let errorExist = false;
      if (editPracticeInfoEmailSchema?.length === 0) {
        const displayId =
          Date.now().toString(20) + Math.random().toString(20).substring(2, 5);
        const newSchema = getInitialSchema({
          email: "",
          contactType: "",
          displayId,
        });
        setEditPracticeInfoPhoneSchema([...newSchema]);
        return;
      }

      const emptyEmailField = editPracticeInfoEmailSchema.find(
        (item) => item?.name === "email" && item?.defaultValue?.length === 0
      );

      const emailErorExist = editPracticeInfoEmailSchema.find(
        (item) => item?.customErrorMsg?.length > 0
      );

      if (emptyEmailField || emailErorExist?.customErrorMsg?.length > 0) {
        setDisableSaveBtn(false);
        setErrorMsg("");
        errorExist = true;
      }
      if (!errorExist) {
        setDisableSaveBtn(true);
        setErrorMsg("");
      } else {
        setDisableSaveBtn(false);
      }
    }
  }, [
    editPracticeInfoPhoneSchema,
    editPracticeInfoFaxSchema,
    editPracticeInfoEmailSchema,
  ]);

  useEffect(() => {
    if (practiceInfoPhoneFaxDetails) {
      const phoneSchemaData = [];
      const faxSchemaData = [];
      const practiceInfoPhoneData = practiceInfoPhoneFaxDetails.filter(
        (item) => item.phoneType === "PHONE"
      );
      const practiceInfoFaxData = practiceInfoPhoneFaxDetails.filter(
        (item) => item.phoneType === "FAX"
      );
      setPracticeInfoPhoneData(practiceInfoPhoneData);
      setPracticeInfoFaxData(practiceInfoFaxData);
      practiceInfoPhoneData.map((defaultValues) => {
        phoneSchemaData.push(...getInitialSchema(defaultValues));
      });
      setEditPracticeInfoPhoneSchema(phoneSchemaData);
      if (practiceInfoFaxData?.length > 0) {
        practiceInfoFaxData.map((defaultValues) => {
          faxSchemaData.push(...getInitialSchema(defaultValues));
        });
        setEditPracticeInfoFaxSchema(faxSchemaData);
      }
      if (practiceInfoFaxData?.length === 0) {
        const displayId =
          Date.now().toString(20) + Math.random().toString(20).substring(2, 5);
        const defaultValues = {
          phoneType: "FAX",
          displayId,
        };
        faxSchemaData.push(...getInitialSchema(defaultValues));
        setEditPracticeInfoFaxSchema(faxSchemaData);
      }
    }
    if (practiceInfoEmailDetails) {
      const emailSchemaData = [];
      if (practiceInfoEmailDetails?.length === 0) {
        const displayId =
          Date.now().toString(20) + Math.random().toString(20).substring(2, 5);
        const defaultValues = {
          email: "",
          displayId,
        };
        emailSchemaData.push(...getInitialSchema(defaultValues));
        setEditPracticeInfoEmailSchema(emailSchemaData);
        return;
      }

      if (practiceInfoEmailDetails?.length > 0) {
        practiceInfoEmailDetails.map((item) => {
          emailSchemaData.push(...getInitialSchema(item));
        });
        setEditPracticeInfoEmailSchema(emailSchemaData);
      }
    }
  }, [practiceInfoPhoneFaxDetails, practiceInfoEmailDetails, openPopup]);

  useEffect(() => {
    if (practiceInfoDataError || emailDataError || phoneFaxDataError) {
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 2000);
    }
  }, [practiceInfoDataError, phoneFaxDataError, emailDataError]);

  useEffect(() => {
    if (
      !isPracticeInfoDataLoading &&
      !isPhoneFaxDataLoading &&
      !isEmailDataLoading
    ) {
      const hasMissingData =
        !practiceInfoDetails?.name ||
        !practiceInfoDetails?.street1 ||
        !practiceInfoDetails?.city ||
        !practiceInfoDetails?.state ||
        !practiceInfoDetails?.postalCode ||
        practiceInfoPhoneData.length === 0 ||
        practiceInfoFaxData.length === 0 ||
        !practiceInfoEmailDetails ||
        practiceInfoEmailDetails.length === 0 ||
        !practiceInfoDetails?.website;

      setMissingData(hasMissingData);
    }
  }, [
    practiceInfoDetails,
    practiceInfoPhoneData,
    practiceInfoFaxData,
    practiceInfoEmailDetails,
    isPracticeInfoDataLoading,
    isPhoneFaxDataLoading,
    isEmailDataLoading,
  ]);

  const getPopupWidth = () => {
    if (
      currentSection === "practiceInfoName" ||
      currentSection === "practiceInfoWebsite"
    ) {
      return "50%";
    }
    return "70%";
  };

  const handleOnChange = (name, value) => {
    formRef.current[name] = value;
    if (formRef?.current?.name?.length === 0) {
      setDisableSaveBtn(false);
      return;
    }
    if (
      formRef?.current?.street1?.length === 0 ||
      formRef?.current?.city?.length === 0 ||
      formRef?.current?.postalCode?.length === 0 ||
      (formRef?.current?.postalCode?.length > 0 &&
        !validPostalCode.test(formRef?.current?.postalCode)) ||
      formRef?.current?.postalCode?.length > 5
    ) {
      setDisableSaveBtn(false);
      return;
    }
    if (
      formRef?.current?.website?.length > 0 &&
      !validUrl.test(formRef?.current?.website)
    ) {
      setDisableSaveBtn(false);
      setErrorMsg("Please enter a valid url");
      return;
    }
    setErrorMsg("");
    setDisableSaveBtn(true);
  };

  const getFormClassName = () => {
    if (
      currentSection === "practiceInfoName" ||
      currentSection === "practiceInfoAddress" ||
      currentSection === "practiceInfoWebsite"
    ) {
      return "practiceInfoFormWrapper";
    }
    if (
      currentSection === "practiceInfoPhone" ||
      currentSection === "practiceInfoFax" ||
      currentSection === "practiceInfoEmail"
    ) {
      return "practiceInfoPhoneFaxEmailFormWrapper";
    }
  };

  const getPopupFormHeader = () => {
    if (currentSection === "practiceInfoName") {
      return "PRACTICE NAME";
    }
    if (currentSection === "practiceInfoAddress") {
      return "PRACTICE ADDRESS";
    }
    if (currentSection === "practiceInfoPhone") {
      return "PHONE NUMBER";
    }
    if (currentSection === "practiceInfoFax") {
      return "FAX NUMBER";
    }
    if (currentSection === "practiceInfoEmail") {
      return "EMAIL ADDRESS";
    }
    if (currentSection === "practiceInfoWebsite") {
      return "WEBSITE";
    }
  };

  const getFieldsProps = (defaultValues) => {
    if (
      defaultValues.phoneType === "PHONE" ||
      defaultValues.phoneType === "FAX"
    ) {
      const defaultValueInNANPFormat =
        convertTenDigitPhoneNumberToNANP(defaultValues.number) || "";

      return {
        label: defaultValues.phoneType === "PHONE" ? "PHONE" : "FAX",
        name: "number",
        type: "number",
        maxLength: 12,
        defaultValue:
          convertTenDigitPhoneNumberToNANP(defaultValues.number) || "",
        value: defaultValues.value || "",
        customPhoneNumberDisplay: true,
        className:
          defaultValueInNANPFormat?.length !== 12 ? "phoneErrorBorder" : "",
      };
    }
    if ("email" in defaultValues) {
      const isValidEmail = validEmail.test(defaultValues.email || "");
      return {
        label: "EMAIL",
        name: "email",
        type: "email",
        defaultValue: defaultValues.email || "",
        value: defaultValues.value || "",
        validateEmail: true,
        className: !isValidEmail ? "phoneErrorBorder" : "",
      };
    }
  };

  const getInitialSchema = (defaultValues) => {
    const {
      label,
      name,
      type,
      defaultValue,
      value,
      className,
      customPhoneNumberDisplay,
    } = getFieldsProps(defaultValues);
    return [
      {
        displayId: defaultValues.displayId,
        component: "input",
        type,
        label,
        name,
        defaultValue,
        customPhoneNumberDisplay,
        value,
        maxLength: type === "number" ? 12 : null,
        placeholder: " ",
        onKeyChangeCallback: (name, value) =>
          handleInputChange(value, defaultValues),
        valueChangeCallback: () => {},
        customErrorMsg: "",
        required: true,
        className: className,
      },
      {
        displayId: defaultValues.displayId,
        component: "dropdown",
        label: "CONTACT TYPE",
        name: "contactType",
        dropdownOptions: practiceInfoContactTypeDropdownOptions,
        defaultValue:
          practiceInfoContactTypeDropdownOptions.find(
            (option) => option?.value === defaultValues?.contactType
          )?.value || "",
        onSelectCallBack: (name, value) =>
          handleSelectContactType(value, defaultValues),
        required: false,
      },
      {
        displayId: defaultValues.displayId,
        component: "input",
        btnClassName: "deleteButton",
        type: "button",
        name: "delete",
        label: "Delete",
        handleButtonClickCallback: () => handleDeleteSchemaData(defaultValues),
      },
    ];
  };

  const handlePhoneNumberInputChange = (prev, value, defaultValues) => {
    const isValidNumber =
      value && value.length === 12 && validNumber.test(value);
    const numberCounts = {};
    prev
      .filter((item) => item.name === "number")
      .map((item) => {
        const numberValue =
          item.displayId === defaultValues.displayId
            ? value
            : item.defaultValue;

        if (
          numberValue &&
          numberValue.length === 12 &&
          validNumber.test(numberValue)
        ) {
          numberCounts[numberValue] = (numberCounts[numberValue] || 0) + 1;
        }
      });

    return prev.map((item) => {
      if (item.name !== "number") return item;

      if (item.displayId === defaultValues.displayId) {
        const updatedItem = {
          ...item,
          defaultValue: value,
          className: !value || value.length !== 12 ? "phoneErrorBorder" : "",
        };

        if (!isValidNumber) {
          setDisableSaveBtn(false);
        }

        if (numberCounts[value] > 1) {
          setDisableSaveBtn(false);
          return {
            ...updatedItem,
            customErrorMsg: "This number already exists.",
          };
        }

        const disableBtn =
          Object.values(numberCounts).filter((count) => count > 1).length === 0;
        setDisableSaveBtn(disableBtn);

        return {
          ...updatedItem,
          customErrorMsg: "",
        };
      }

      const itemNumberValue = item.defaultValue;

      if (
        itemNumberValue &&
        (itemNumberValue.length !== 12 || !validNumber.test(itemNumberValue))
      ) {
        setDisableSaveBtn(false);
      }

      if (
        itemNumberValue &&
        itemNumberValue.length === 12 &&
        validNumber.test(itemNumberValue) &&
        numberCounts[itemNumberValue] > 1
      ) {
        setDisableSaveBtn(false);
        return {
          ...item,
          customErrorMsg: "This number already exists.",
        };
      }

      return {
        ...item,
        customErrorMsg: "",
      };
    });
  };

  const handleFaxNumberInputChange = (prev, value, defaultValues) => {
    const isValidNumber =
      value && value.length === 12 && validNumber.test(value);

    const numberCounts = {};
    prev
      .filter((item) => item.name === "number")
      .map((item) => {
        const numberValue =
          item.displayId === defaultValues.displayId
            ? value
            : item.defaultValue;

        if (
          numberValue &&
          numberValue.length === 12 &&
          validNumber.test(numberValue)
        ) {
          numberCounts[numberValue] = (numberCounts[numberValue] || 0) + 1;
        }
      });

    return prev.map((item) => {
      if (item.name !== "number") return item;

      if (item.displayId === defaultValues.displayId) {
        const updatedItem = {
          ...item,
          defaultValue: value,
          className: !value || value.length !== 12 ? "phoneErrorBorder" : "",
        };

        if (!isValidNumber) {
          setDisableSaveBtn(false);
        }

        if (numberCounts[value] > 1) {
          setDisableSaveBtn(false);
          return {
            ...updatedItem,
            customErrorMsg: "This number already exists.",
          };
        }

        const disableBtn =
          Object.values(numberCounts).filter((count) => count > 1).length === 0;
        setDisableSaveBtn(disableBtn);
        return {
          ...updatedItem,
          customErrorMsg: "",
        };
      }

      const itemNumberValue = item.defaultValue;

      if (
        itemNumberValue &&
        (itemNumberValue.length !== 12 || !validNumber.test(itemNumberValue))
      ) {
        setDisableSaveBtn(false);
      }

      if (
        itemNumberValue &&
        itemNumberValue.length === 12 &&
        validNumber.test(itemNumberValue) &&
        numberCounts[itemNumberValue] > 1
      ) {
        setDisableSaveBtn(false);
        return {
          ...item,
          customErrorMsg: "This number already exists.",
        };
      }

      return {
        ...item,
        customErrorMsg: "",
      };
    });
  };

  const handleOfficialEmailInputChange = (prev, value, defaultValues) => {
    const isValidEmailFormat = validEmail.test(value);
    const emailCounts = {};
    prev
      .filter((item) => item.name === "email")
      .map((item) => {
        const emailValue =
          item.displayId === defaultValues.displayId
            ? value
            : item.defaultValue;
        if (validEmail.test(emailValue)) {
          emailCounts[emailValue] = (emailCounts[emailValue] || 0) + 1;
        }
      });

    return prev.map((item) => {
      if (item.name !== "email") return item;

      if (item.displayId === defaultValues.displayId) {
        const updatedItem = {
          ...item,
          defaultValue: value,
          className: !value  ? "phoneErrorBorder" : "",
        };

        if (!isValidEmailFormat) {
          setDisableSaveBtn(false);
          return {
            ...updatedItem,
            className: "phoneErrorBorder",
            customErrorMsg: "Please enter a valid email address",
          };
        }
        if (emailCounts[value] === 2) {
          setDisableSaveBtn(false);
          return {
            ...updatedItem,
            customErrorMsg: "This email already exists.",
          };
        }

        const disableBtn =
          Object.values(emailCounts).filter((count) => count > 1).length === 0;
        setDisableSaveBtn(disableBtn);
        return {
          ...updatedItem,
          customErrorMsg: "",
        };
      }

      const itemEmailValue = item.defaultValue;
      const isValidEmail = validEmail.test(itemEmailValue);

      if (itemEmailValue && !isValidEmail) {
        setDisableSaveBtn(false);
        return {
          ...item,
          className: "phoneErrorBorder",
          customErrorMsg: "Please enter a valid email address",
        };
      }

      if (
        itemEmailValue &&
        isValidEmail &&
        emailCounts[itemEmailValue] > 1
      ) {
        setDisableSaveBtn(false);
        return {
          ...item,
          customErrorMsg: "This email already exists.",
        };
      }
      return {
        ...item,
        className: "",
        customErrorMsg: "",
      };
    });
  };

  const handleInputChange = (value, defaultValues) => {
    if (defaultValues.phoneType === "PHONE") {
      setEditPracticeInfoPhoneSchema((prev) =>
        handlePhoneNumberInputChange(prev, value, defaultValues)
      );
    }
    if (defaultValues.phoneType === "FAX") {
      setEditPracticeInfoFaxSchema((prev) =>
        handleFaxNumberInputChange(prev, value, defaultValues)
      );
    }
    if ("email" in defaultValues) {
      setEditPracticeInfoEmailSchema((prev) =>
        handleOfficialEmailInputChange(prev, value, defaultValues)
      );
    }
  };

  const findAllMainContacts = (schema, currentDisplayId, newValue) => {
    return schema.filter(
      (item) =>
        item.name === "contactType" &&
        ((item.displayId === currentDisplayId && newValue === "MAIN") ||
          (item.displayId !== currentDisplayId && item.defaultValue === "MAIN"))
    );
  };

  const updateSchemaWithMainContactErrors = (schema, defaultValues, value) => {
    const mainContacts = findAllMainContacts(
      schema,
      defaultValues.displayId,
      value
    );
    const hasMultipleMain = mainContacts.length > 1;
    return schema.map((item) => {
      if (item.name === "contactType") {
        if (item.displayId === defaultValues.displayId) {
          const newValue = practiceInfoContactTypeDropdownOptions.find(
            (option) => option?.value === value
          )?.value;

          return {
            ...item,
            defaultValue: newValue,
            customErrorMsg:
              hasMultipleMain && newValue === "MAIN"
                ? "Only one record can have contact type 'MAIN'"
                : "",
          };
        }

        const isMainContact = item.defaultValue === "MAIN";
        if (isMainContact && hasMultipleMain) {
          setDisableSaveBtn(false);
          return {
            ...item,
            customErrorMsg: "Only one record can have contact type 'MAIN'",
          };
        }

        if (
          item.customErrorMsg === "Only one record can have contact type 'MAIN'"
        ) {
          setDisableSaveBtn(true);
          return {
            ...item,
            customErrorMsg: "",
          };
        }
      }
      return item;
    });
  };

  const handleSelectContactType = (value, defaultValues) => {
    if (defaultValues.phoneType === "PHONE") {
      setEditPracticeInfoPhoneSchema((prev) => {
        return updateSchemaWithMainContactErrors(prev, defaultValues, value);
      });
      return;
    }
    if (defaultValues.phoneType === "FAX") {
      setEditPracticeInfoFaxSchema((prev) => {
        return updateSchemaWithMainContactErrors(prev, defaultValues, value);
      });
      return;
    }
    if ("email" in defaultValues) {
      setEditPracticeInfoEmailSchema((prev) => {
        return updateSchemaWithMainContactErrors(prev, defaultValues, value);
      });
    }
  };

  const handleAddSchema = () => {
    if (currentSection === "practiceInfoPhone") {
      const displayId =
        Date.now().toString(20) + Math.random().toString(20).substring(2, 5);
      const newSchema = getInitialSchema({ phoneType: "PHONE", displayId });
      setEditPracticeInfoPhoneSchema((prev) => [...prev, ...newSchema]);
      return;
    }
    if (currentSection === "practiceInfoFax") {
      const displayId =
        Date.now().toString(20) + Math.random().toString(20).substring(2, 5);
      const newSchema = getInitialSchema({ phoneType: "FAX", displayId });
      setEditPracticeInfoFaxSchema((prev) => [...prev, ...newSchema]);
      return;
    }
    if (currentSection === "practiceInfoEmail") {
      const displayId =
        Date.now().toString(20) + Math.random().toString(20).substring(2, 5);
      const newSchema = getInitialSchema({ email: "", displayId });
      setEditPracticeInfoEmailSchema((prev) => [...prev, ...newSchema]);
    }
  };

  const getUpdatedPhoneNumberSchema = (schema, defaultValues) => {
    const currentPhoneNumberSchema = schema.filter(
      (item) => item.displayId !== defaultValues.displayId
    );
    const numberCounts = {};
    const mainContactCounts = {};
    const emailCounts = {};
    currentPhoneNumberSchema.map((item) => {
      if (item.name === "number" && item.defaultValue) {
        numberCounts[item.defaultValue] =
          (numberCounts[item.defaultValue] || 0) + 1;
      }
      if (item.name === "email" && item.defaultValue) {
        emailCounts[item.defaultValue] =
          (emailCounts[item.defaultValue] || 0) + 1;
      }
      if (item.name === "contactType" && item.defaultValue === "MAIN") {
        mainContactCounts["MAIN"] = (mainContactCounts["MAIN"] || 0) + 1;
      }
    });

    return currentPhoneNumberSchema.map((item) => {
      if (item.name === "number") {
        const numberExist = numberCounts[item.defaultValue] || 0;
        return {
          ...item,
          customErrorMsg:
            numberExist > 1 ? "This phone number already exists." : "",
        };
      }
      if (item.name === "email") {
        const emailExist = emailCounts[item.defaultValue] || 0;
        return {
          ...item,
          customErrorMsg: emailExist > 1 ? "This email already exists." : "",
        };
      }
      if (item.name === "contactType" && item.defaultValue === "MAIN") {
        const mainExists = mainContactCounts["MAIN"] || 0;
        return {
          ...item,
          customErrorMsg:
            mainExists > 1
              ? "Only one record can have contact type 'MAIN'"
              : "",
        };
      }
      return item;
    });
  };

  const handleDeleteSchemaData = (defaultValues) => {
    if (defaultValues.phoneType === "PHONE") {
      setEditPracticeInfoPhoneSchema((prev) =>
        getUpdatedPhoneNumberSchema(prev, defaultValues)
      );
      return;
    }
    if (defaultValues.phoneType === "FAX") {
      setEditPracticeInfoFaxSchema((prev) =>
        getUpdatedPhoneNumberSchema(prev, defaultValues)
      );
      return;
    }
    if ("email" in defaultValues) {
      setEditPracticeInfoEmailSchema((prev) =>
        getUpdatedPhoneNumberSchema(prev, defaultValues)
      );
    }
  };

  const handleEditInfoButton = (section) => {
    setDisableSaveBtn(true);
    setErrorMsg("");
    setCurrentSection(section);
    setOpenPopup(true);
    if (section === "practiceInfoPhone") {
      formRef.current.initialSchema = editPracticeInfoPhoneSchema;
      return;
    }
    if (section === "practiceInfoFax") {
      formRef.current.initialSchema = editPracticeInfoFaxSchema;
      return;
    }
    if (section === "practiceInfoEmail") {
      formRef.current.initialSchema = editPracticeInfoEmailSchema;
    }
  };

  const handleSchema = () => {
    if (currentSection === "practiceInfoName") {
      return EditPracticeInfoNameSchema;
    }
    if (currentSection === "practiceInfoAddress") {
      return EditPracticeInfoAddressSchema;
    }
    if (currentSection === "practiceInfoPhone") {
      return editPracticeInfoPhoneSchema;
    }
    if (currentSection === "practiceInfoFax") {
      return editPracticeInfoFaxSchema;
    }
    if (currentSection === "practiceInfoEmail") {
      return editPracticeInfoEmailSchema;
    }
    if (currentSection === "practiceInfoWebsite") {
      return EditPracticeInfoWebsiteSchema;
    }
  };

  const handlePhoneFaxEmailFormData = (schema) => {
    let updatedRequiredData;
    if (
      currentSection === "practiceInfoPhone" ||
      currentSection === "practiceInfoFax"
    ) {
      updatedRequiredData = schema.map((items) => {
        if (items.name === "number") {
          const contactTypeItem = schema.find(
            (contact) =>
              contact.displayId === items.displayId &&
              contact.name === "contactType"
          );
          if (contactTypeItem) {
            const updatedData = {
              displayId: items.displayId,
              number: items.defaultValue,
              contactType: contactTypeItem.defaultValue,
              phoneType:
                currentSection === "practiceInfoPhone" ? "PHONE" : "FAX",
              isValid: true,
              createdFromSource: "BRIGHTER",
            };
            if (updatedData.contactType.length === 0) {
              delete updatedData.contactType;
            }
            return updatedData;
          }
        }
      });
    }
    if (currentSection === "practiceInfoEmail") {
      updatedRequiredData = schema.map((items) => {
        if (items.name === "email") {
          const contactTypeItem = schema.find(
            (contact) =>
              contact.displayId === items.displayId &&
              contact.name === "contactType"
          );
          if (contactTypeItem) {
            const payload = {
              email: items.defaultValue,
              displayId: items.displayId,
              isValid: true,
            };
            if (contactTypeItem.defaultValue) {
              payload.contactType = contactTypeItem.defaultValue;
            }
            return payload;
          }
        }
      });
    }
    const updatedData = updatedRequiredData.filter((data) => data != undefined);
    return updatedData;
  };

  const getWebsiteUrl = (formData) => {
    if (
      formData?.website.startsWith("http://") ||
      formData?.website.startsWith("https://")
    ) {
      return formData?.website;
    }
    if (formData?.website?.length > 0) {
      return `https://${formData?.website}`;
    }
  };

  const findModifiedPhoneFaxOrContactTypes = (modifiedArray, previousArray) => {
    const previousPhoneFaxData = {};
    const previousContactTypeMap = {};
    const previousDisplayIds = [];
    const deletedItems = [];
    previousArray.forEach((item) => {
      if (item.displayId && !previousDisplayIds.includes(item.displayId)) {
        previousDisplayIds.push(item.displayId);
      }
      if (item.name === "number" && item.component === "input") {
        previousPhoneFaxData[item.displayId] = item.defaultValue;
      }
      if (item.name === "contactType" && item.component === "dropdown") {
        previousContactTypeMap[item.displayId] = item.defaultValue;
      }
    });

    const currentDisplayIds = modifiedArray.map((item) => item.displayId);

    previousDisplayIds.forEach((displayId) => {
      if (
        !currentDisplayIds.includes(displayId) &&
        previousPhoneFaxData[displayId]
      ) {
        const deletedItem = {
          number: previousPhoneFaxData[displayId],
          contactType: previousContactTypeMap[displayId],
          phoneType: currentSection === "practiceInfoPhone" ? "PHONE" : "FAX",
          isValid: false,
        };
        deletedItems.push(deletedItem);
      }
    });

    const modifiedEntries = modifiedArray.filter((item) => {
      const isNewItem = !previousDisplayIds.includes(item.displayId);

      if (isNewItem) {
        return true;
      }

      const previousPhoneFax = previousPhoneFaxData[item.displayId];
      const previousContactType = previousContactTypeMap[item.displayId];

      const phoneFaxChanges =
        previousPhoneFax && item.number !== previousPhoneFax;

      const contactTypeChanged =
        (previousContactType || item.contactType) &&
        previousContactType !== item.contactType;

      if (phoneFaxChanges || contactTypeChanged) {
        item.previousData = {
          contactType: previousContactType,
          inValidReason: "USER_EDIT",
          isValid: false,
          number: previousPhoneFax,
          phoneType: currentSection === "practiceInfoPhone" ? "PHONE" : "FAX",
        };
      }

      return phoneFaxChanges || contactTypeChanged;
    });
    return [...modifiedEntries, ...deletedItems];
  };

  const findModifiedEmailsOrContactTypes = (modifiedArray, previousArray) => {
    const previousEmailMap = {};
    const previousContactTypeMap = {};
    const previousDisplayIds = [];
    const deletedItems = [];

    previousArray.forEach((item) => {
      if (item.displayId && !previousDisplayIds.includes(item.displayId)) {
        previousDisplayIds.push(item.displayId);
      }
      if (item.name === "email" && item.component === "input") {
        previousEmailMap[item.displayId] = item.defaultValue;
      }
      if (item.name === "contactType" && item.component === "dropdown") {
        previousContactTypeMap[item.displayId] = item.defaultValue;
      }
    });

    const currentDisplayIds = modifiedArray.map((item) => item.displayId);

    previousDisplayIds.forEach((displayId) => {
      if (
        !currentDisplayIds.includes(displayId) &&
        previousEmailMap[displayId]
      ) {
        const deletedItem = {
          email: previousEmailMap[displayId],
          contactType: previousContactTypeMap[displayId],
          isValid: false,
        };
        deletedItems.push(deletedItem);
      }
    });

    const modifiedEntries = modifiedArray.filter((item) => {
      const isNewItem = !previousDisplayIds.includes(item.displayId);

      if (isNewItem) {
        return true;
      }

      const previousEmail = previousEmailMap[item.displayId];
      const previousContactType = previousContactTypeMap[item.displayId];

      const emailChanged = previousEmail && item.email !== previousEmail;
      const contactTypeChanged =
        (previousContactType || item.contactType) &&
        previousContactType !== item.contactType;

      if (emailChanged || contactTypeChanged) {
        item.previousData = {
          email: previousEmail,
          contactType: previousContactType,
          isValid: false,
        };
        if (previousContactType) {
          item.previousData.contactType = previousContactType;
        }
      }
      return emailChanged || contactTypeChanged;
    });

    return [...modifiedEntries, ...deletedItems];
  };

  const handleSubmitPracticeInfoForm = async (formData) => {
    if (currentSection === "practiceInfoName") {
      const payload = {
        ...practiceInfoDetails,
        name: formData?.name,
      };
      await dispatch(
        fetchUpdatePracticeInfoDetailsAPI({ practiceId, payload })
      );
      setOpenPopup(!openPopup);
    }
    if (currentSection === "practiceInfoAddress") {
      const { street1, street2, city, state, postalCode } = formData;
      const payload = {
        ...practiceInfoDetails,
        street1,
        street2,
        city,
        state,
        postalCode,
      };
      await dispatch(
        fetchUpdatePracticeInfoDetailsAPI({ practiceId, payload })
      );
      setOpenPopup(!openPopup);
    }
    if (currentSection === "practiceInfoPhone") {
      const contactTypeExist = editPracticeInfoPhoneSchema.filter(
        (item) => item?.name === "contactType" && item?.defaultValue === "MAIN"
      );
      if (contactTypeExist?.length === 0) {
        setErrorMsg("Please select a ‘MAIN’ phone number");
        return;
      }
      const updatedFormData = handlePhoneFaxEmailFormData(
        editPracticeInfoPhoneSchema
      );
      const modifiedPhoneFaxEntries = findModifiedPhoneFaxOrContactTypes(
        updatedFormData,
        formRef.current.initialSchema
      );
      const filteredPayload = [];
      modifiedPhoneFaxEntries.map((items) => {
        delete items.displayId;
        if (items.previousData) {
          filteredPayload.push(items.previousData);
          delete items.previousData;
        }
        filteredPayload.push(items);
        return items;
      });
      let filteredPhoneNumberArray = filteredPayload?.map((item) => {
        return {
          ...item,
          number: item.number.replace(/-/g, ""),
        };
      });

      const payload = {
        practiceUpdatePhoneRequest: {
          phoneNumbers: filteredPhoneNumberArray,
        },
      };
      await dispatch(
        fetchUpdatePracticeInfoPhoneFaxDetails({ practiceId, payload })
      ).then(() => dispatch(fetchPracticeInfoPhoneFaxDetails(practiceId)));
      setOpenPopup(!openPopup);
    }
    if (currentSection === "practiceInfoFax") {
      const updatedFormData = handlePhoneFaxEmailFormData(
        editPracticeInfoFaxSchema
      );
      const modifiedPhoneFaxEntries = findModifiedPhoneFaxOrContactTypes(
        updatedFormData,
        formRef.current.initialSchema
      );
      const filteredPayload = [];
      modifiedPhoneFaxEntries.map((items) => {
        delete items.displayId;
        if (items.previousData) {
          filteredPayload.push(items.previousData);
          delete items.previousData;
        }
        filteredPayload.push(items);
        return items;
      });

      // if (filteredPayload.length > 0) &&
      let filteredPhoneNumberArray = filteredPayload?.map((item) => {
        return {
          ...item,
          number: item.number.replace(/-/g, ""),
        };
      });

      const payload = {
        practiceUpdatePhoneRequest: {
          phoneNumbers: filteredPhoneNumberArray,
        },
      };
      await dispatch(
        fetchUpdatePracticeInfoPhoneFaxDetails({ practiceId, payload })
      ).then(() => dispatch(fetchPracticeInfoPhoneFaxDetails(practiceId)));
      setOpenPopup(!openPopup);
    }
    if (currentSection === "practiceInfoEmail") {
      const updatedFormData = handlePhoneFaxEmailFormData(
        editPracticeInfoEmailSchema
      );
      const modifiedEmailEntries = findModifiedEmailsOrContactTypes(
        updatedFormData,
        formRef.current.initialSchema
      );
      const filteredPayload = [];
      modifiedEmailEntries.map((items) => {
        delete items.displayId;
        if (items.previousData) {
          filteredPayload.push(items.previousData);
          delete items.previousData;
        }
        filteredPayload.push(items);
        return items;
      });

      const payload = {
        emails: filteredPayload || [],
      };
      await dispatch(
        fetchUpdatePracticeInfoEmailDetailsAPI({ practiceId, payload })
      ).then(() => dispatch(fetchPracticeInfoEmailDetails(practiceId)));
      setOpenPopup(!openPopup);
    }
    if (currentSection === "practiceInfoWebsite") {
      const websiteUrl = getWebsiteUrl(formData);
      const payload = {
        ...practiceInfoDetails,
        website: websiteUrl || "",
      };
      await dispatch(
        fetchUpdatePracticeInfoDetailsAPI({ practiceId, payload })
      );
      setOpenPopup(!openPopup);
    }
    setDisableSaveBtn(true);
    setErrorMsg("");
  };

  const EditPracticeInfoNameSchema = [
    {
      component: "input",
      label: "PRACTICE NAME",
      type: "text",
      name: "name",
      required: true,
      placeholder: " ",
      defaultValue: practiceInfoDetails?.name || "",
      minLength: 1,
      onKeyChangeCallback: (name, value) => {
        handleOnChange(name, value);
      },
      valueChangeCallback: () => {},
    },
  ];

  const EditPracticeInfoAddressSchema = [
    {
      component: "input",
      type: "text",
      label: "STREET ADDRESS 1",
      placeholder: " ",
      name: "street1",
      defaultValue: practiceInfoDetails?.street1 || "",
      onKeyChangeCallback: (name, value) => {
        handleOnChange(name, value);
      },
      valueChangeCallback: () => {},
      required: true,
    },
    {
      component: "input",
      type: "text",
      label: "STREET ADDRESS 2",
      placeholder: " ",
      name: "street2",
      defaultValue: practiceInfoDetails?.street2 || "",
      valueChangeCallback: () => {},
    },
    {
      component: "input",
      type: "text",
      label: "CITY",
      placeholder: " ",
      name: "city",
      defaultValue: practiceInfoDetails?.city || "",
      onKeyChangeCallback: (name, value) => {
        handleOnChange(name, value);
      },
      valueChangeCallback: () => {},
      required: true,
    },
    {
      component: "dropdown",
      label: "STATE",
      name: "state",
      dropdownOptions: statesData,
      defaultValue: practiceInfoDetails?.state || "",
      valueChangeCallback: () => {},
      required: true,
    },
    {
      component: "input",
      type: "text",
      label: "ZIPCODE",
      placeholder: " ",
      name: "postalCode",
      minLength: 1,
      defaultValue: practiceInfoDetails?.postalCode || "",
      onKeyChangeCallback: (name, value) => handleOnChange(name, value),
      valueChangeCallback: () => {},
      required: true,
    },
  ];

  const EditPracticeInfoWebsiteSchema = [
    {
      component: "input",
      label: "WEBSITE URL",
      type: "text",
      name: "website",
      placeholder: " ",
      defaultValue: practiceInfoDetails?.website || "",
      onKeyChangeCallback: (name, value) => {
        handleOnChange(name, value);
      },
      valueChangeCallback: () => {},
    },
  ];

  const RenderPracticeInfoNameDetails = () => {
    if (isPracticeInfoDataLoading) return null;
    return (
      <div className="practice-info">
        <div>
          <p className="info-title">PRACTICE NAME</p>
          <p className="info-value">{practiceInfoDetails?.name}</p>
        </div>
        <div className="edit-info">
          <button onClick={() => handleEditInfoButton("practiceInfoName")}>
            Edit Info
          </button>
        </div>
      </div>
    );
  };

  const RenderPracticeInfoError = () => {
    if (!showError) return null;
    return (
      <div
        className="overlay-alert alert alert-danger alertTopPosition"
        role="alert"
      >
        {practiceInfoDataErrorMsg || phoneFaxDataErrorMsg || emailDataErrorMsg}
      </div>
    );
  };

  const RenderMissingDataAlert = () => {
    if (!missingData) return null;
    return (
      <div className="missing-data-alert col-xs-6 col-sm-6 col-md-8 col-lg-9">
        <img src={redAlert} alt="Alert" />
        <span>Missing Data</span>
      </div>
    );
  };

  const RenderPracticeInfoDetails = () => {
    return (
      <div className="full-width-container top-thirty">
        {RenderMissingDataAlert()}
        <div className="row border">
          <div className="col-xs-6 col-sm-6 col-md-8 col-lg-9">
            {RenderPracticeInfoError()}
            {RenderPracticeInfoNameDetails()}
            {RenderPracticeInfoAddressDetails()}
            {RenderPracticeInfoPhoneDetails()}
            {RenderPracticeInfoFaxDetails()}
            {RenderPracticeInfoOfficeEmailDetails()}
            {RenderPracticeInfoWebsiteDetails()}
          </div>
          <div className="col-xs-6 col-sm-6 col-md-4 col-lg-3">
            <div className="practice-map">
              <div style={{ padding: "24px" }}>
                {practiceLatLng.length > 0 && (
                  <GoogleMapComponent
                    latLngList={practiceLatLng}
                    resizeImg={false}
                    zoomLevel={15}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const RenderPracticeInfoAddressDetails = () => {
    if (isPracticeInfoDataLoading) return null;
    return (
      <div className="practice-info">
        <div>
          <p className="info-title">ADDRESS</p>
          <p className="info-value" aria-label="Practice address">
            {`${practiceInfoDetails?.street1}${
              practiceInfoDetails?.street2
                ? `, ${practiceInfoDetails.street2}`
                : ""
            }${
              practiceInfoDetails?.city ? `, ${practiceInfoDetails.city}` : ""
            }${
              practiceInfoDetails?.state ? `, ${practiceInfoDetails.state}` : ""
            }${
              practiceInfoDetails?.postalCode
                ? `, ${practiceInfoDetails.postalCode}`
                : ""
            }`}
          </p>
        </div>
        <div className="edit-info">
          <button onClick={() => handleEditInfoButton("practiceInfoAddress")}>
            Edit Info
          </button>
        </div>
      </div>
    );
  };

  const RenderPracticeInfoPhoneDetails = () => {
    if (isPhoneFaxDataLoading) return null;
    return (
      <div className="practice-info">
        <div className="phoneNumber">
          <div className="edit-info">
            <button onClick={() => handleEditInfoButton("practiceInfoPhone")}>
              Edit Info
            </button>
          </div>
          <p className="info-title">PHONE</p>
          {practiceInfoPhoneData.map((phoneData, index) => (
            <p className="info-value" key={index}>
              <span className="phone-number">
                {convertTenDigitPhoneNumberToNANP(phoneData?.number)}
              </span>{" "}
              <span className="contact-type">{phoneData?.contactType}</span>
              <br />
            </p>
          ))}
        </div>
      </div>
    );
  };

  const RenderPracticeInfoFaxDetails = () => {
    if (isPhoneFaxDataLoading) return null;
    return (
      <div className="practice-info">
        <div className="phoneNumber">
          <div className="edit-info">
            <button onClick={() => handleEditInfoButton("practiceInfoFax")}>
              {practiceInfoFaxData?.length > 0 ? "Edit Info" : "Add Fax Number"}
            </button>
          </div>
          <p
            className={`info-title ${
              practiceInfoFaxData?.length === 0 && "error-label"
            }`}
          >
            FAX
          </p>
          {practiceInfoFaxData.map((faxData, index) => (
            <p className="info-value" key={index}>
              <span className="phone-number">
                {convertTenDigitPhoneNumberToNANP(faxData?.number)}
              </span>
              <span className="contact-type">{faxData?.contactType}</span>
              <br />
            </p>
          ))}
        </div>
      </div>
    );
  };

  const RenderPracticeInfoOfficeEmailDetails = () => {
    if (isEmailDataLoading) return null;
    return (
      <div className="practice-info">
        <div className="phoneNumber">
          <div className="edit-info">
            <button onClick={() => handleEditInfoButton("practiceInfoEmail")}>
              {practiceInfoEmailDetails?.length > 0
                ? "Edit Info"
                : "Add Office Email"}
            </button>
          </div>
          <p
            className={`info-title ${
              practiceInfoEmailDetails?.length === 0 && "error-label"
            }`}
          >
            OFFICE EMAIL
          </p>
          <p className="note">
            For contact purposes only. We never display your email to members or
            share it with third parties.
          </p>
          {practiceInfoEmailDetails &&
            practiceInfoEmailDetails.map((items, index) => (
              <p className="info-value" key={index}>
                <span
                  className={
                    items?.email?.length > 12
                      ? "extendedEmailWidth"
                      : "phone-number"
                  }
                  style={{
                    width: emailWidth,
                    minWidth: emailWidth,
                    maxWidth: emailWidth,
                  }}
                >
                  {items?.email}
                </span>{" "}
                <span className="contact-type">{items?.contactType}</span>
                <br />
              </p>
            ))}
        </div>
      </div>
    );
  };

  const RenderPracticeInfoWebsiteDetails = () => {
    if (isPracticeInfoDataLoading) return null;
    return (
      <div className="practice-info">
        <div>
          <p
            className={`info-title ${
              practiceInfoDetails?.website?.length === 0 && "error-label"
            }`}
          >
            WEBSITE URL
          </p>
          <p className="info-value">
            <a
              target="_blank"
              rel="noreferrer"
              href={practiceInfoDetails?.website}
            >
              {practiceInfoDetails?.website}
            </a>
          </p>
        </div>
        <div className="edit-info">
          <button onClick={() => handleEditInfoButton("practiceInfoWebsite")}>
            {practiceInfoDetails?.website?.length > 0
              ? "Edit Info"
              : "Add Website"}
          </button>
        </div>
      </div>
    );
  };

  const checkDisableAddPhoneNumberSchemaButton = (schema) => {
    return schema.find((phone) => {
      const invalidPhoneNumber =
        phone.name === "number" &&
        phone.defaultValue?.length > 0 &&
        phone.defaultValue?.length !== 12;

      const phoneNumberExist =
        schema.filter(
          (item) =>
            item.name === "number" &&
            item.defaultValue === phone.defaultValue &&
            phone.defaultValue?.length > 0
        ).length > 1;
      return invalidPhoneNumber || phoneNumberExist;
    });
  };

  const RenderAddSchemaButton = () => {
    if (currentSection === "practiceInfoPhone") {
      return (
        <button
          className="addSchemaButton"
          onClick={() => handleAddSchema(currentSection)}
          disabled={
            checkDisableAddPhoneNumberSchemaButton(editPracticeInfoPhoneSchema)
              ? true
              : false
          }
        >
          {"+ ADD PHONE NUMBER"}
        </button>
      );
    }
    if (currentSection === "practiceInfoFax") {
      return (
        <button
          className={`${
            editPracticeInfoFaxSchema?.length === 0
              ? "addSchemaButton addSchemaButtonNoData"
              : "addSchemaButton"
          }`}
          onClick={() => handleAddSchema(currentSection)}
        >
          {"+ADD FAX NUMBER"}
        </button>
      );
    }
    if (currentSection === "practiceInfoEmail") {
      return (
        <button
          className={`${
            editPracticeInfoEmailSchema?.length === 0
              ? "addSchemaButton addSchemaButtonNoData"
              : "addSchemaButton"
          }`}
          onClick={() => handleAddSchema(currentSection)}
        >
          {"+ADD EMAIL ADDRESS"}
        </button>
      );
    }
  };

  const RenderPracticeInfoFormCreator = () => {
    return (
      <>
        <CgFormCreator
          key={handleSchema() || errorMsg}
          schema={handleSchema() || []}
          primaryButton="Save"
          onSubmitCallback={(val) => handleSubmitPracticeInfoForm(val)}
          onCancel={() => {
            formRef.current = {};
            setDisableSaveBtn(true);
            setErrorMsg("");
            setOpenPopup(!openPopup);
          }}
          formClassName={getFormClassName()}
          enablePrimaryBtn={disableSaveBtn}
          showErrorMsg={errorMsg && errorMsg}
          resetFormValues={!openPopup ? true : false}
        />
        {RenderAddSchemaButton()}
      </>
    );
  };

  const RenderPracticeInfoPopupModal = () => {
    return (
      <PopupModal
        isOpen={openPopup}
        onClose={() => {
          formRef.current = {};
          setDisableSaveBtn(true);
          setErrorMsg("");
          setOpenPopup(!openPopup);
        }}
        onSave={() => {}}
        width={getPopupWidth()}
        customRender={RenderPracticeInfoFormCreator}
        popupHeading={getPopupFormHeader()}
      />
    );
  };

  return (
    <>
      {RenderPracticeInfoDetails()}
      {RenderPracticeInfoPopupModal()}
    </>
  );
};

export default PracticeInfo;
